import { apiClient, handleApiSuccess } from './api';
import {
  Club,
  ClubApplication,
  ClubApplicationRequest,
  CreateClubForm,
  Activity,
  Discussion,
  ActivityComment,
  DiscussionReply,
  ClubsResponse
} from '../types';

/**
 * Club Service
 * Handles all club-related API calls
 */
export class ClubService {
  /**
   * Get all clubs with pagination and search
   */
  static async getClubs(search?: string, page: number = 1, limit: number = 20): Promise<ClubsResponse> {
    let url = `/clubs?page=${page}&limit=${limit}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    const response = await apiClient.get(url);
    const data = handleApiSuccess(response);

    return {
      items: data,
      pagination: data.pagination || {},
    };
  }

  /**
   * Get club by ID
   */
  static async getClub(clubId: string): Promise<Club> {
    const response = await apiClient.get(`/clubs/${clubId}`);
    return handleApiSuccess(response);
  }

  /**
   * Create a new club (Faculty only)
   */
  static async createClub(clubData: CreateClubForm): Promise<Club> {
    const response = await apiClient.post('/clubs', clubData);
    return handleApiSuccess(response);
  }

  /**
   * Update club information (Faculty mentor only)
   */
  static async updateClub(clubId: string, clubData: Partial<CreateClubForm>): Promise<Club> {
    const response = await apiClient.put(`/clubs/${clubId}`, clubData);
    return handleApiSuccess(response);
  }

  /**
   * Delete a club (Faculty mentor only)
   */
  static async deleteClub(clubId: string): Promise<void> {
    const response = await apiClient.delete(`/clubs/${clubId}`);
    handleApiSuccess(response);
  }

  /**
   * Get club entry quiz
   */
  static async getClubQuiz(clubId: string): Promise<{ passingScore: number; questions: any[] }> {
    const response = await apiClient.get(`/clubs/${clubId}/quiz`);
    return handleApiSuccess(response);
  }

  /**
   * Submit club application with quiz answers
   */
  static async submitApplication(clubId: string, applicationData: ClubApplicationRequest): Promise<{
    applicationId: string;
    score: number;
    passed: boolean;
    status: string;
  }> {
    const response = await apiClient.post(`/clubs/${clubId}/apply`, applicationData);
    return handleApiSuccess(response);
  }

  /**
   * Get club applications (Club officers only)
   */
  static async getClubApplications(clubId: string): Promise<ClubApplication[]> {
    const response = await apiClient.get(`/clubs/${clubId}/applications`);
    return handleApiSuccess(response);
  }

  /**
   * Approve or reject club application (Club officers only)
   */
  static async reviewApplication(
    clubId: string,
    applicationId: string,
    status: 'approved' | 'rejected',
    memberRole?: string,
    feedback?: string
  ): Promise<void> {
    const response = await apiClient.put(`/clubs/${clubId}/applications/${applicationId}`, {
      status,
      memberRole,
      feedback,
    });
    handleApiSuccess(response);
  }

  /**
   * Get user's club applications
   */
  static async getMyApplications(): Promise<ClubApplication[]> {
    const response = await apiClient.get('/clubs/my-applications');
    return handleApiSuccess(response);
  }

  /**
   * Request reconsideration for rejected application
   */
  static async requestReconsideration(clubId: string, applicationId: string, reason: string): Promise<void> {
    const response = await apiClient.post(`/clubs/${clubId}/applications/${applicationId}/reconsider`, { reason });
    handleApiSuccess(response);
  }

  /**
   * Get club members
   */
  static async getClubMembers(clubId: string): Promise<any[]> {
    const response = await apiClient.get(`/clubs/${clubId}/members`);
    return handleApiSuccess(response);
  }

  /**
   * Update member role (Club officers only)
   */
  static async updateMemberRole(clubId: string, memberId: string, role: string): Promise<void> {
    const response = await apiClient.put(`/clubs/${clubId}/members/${memberId}/role`, { role });
    handleApiSuccess(response);
  }

  /**
   * Remove member from club (Club officers only)
   */
  static async removeMember(clubId: string, memberId: string): Promise<void> {
    const response = await apiClient.delete(`/clubs/${clubId}/members/${memberId}`);
    handleApiSuccess(response);
  }

  // ============ CLUB ACTIVITIES ============

  /**
   * Get club activities
   */
  static async getClubActivities(clubId: string): Promise<Activity[]> {
    const response = await apiClient.get(`/clubs/${clubId}/activities`);
    return handleApiSuccess(response);
  }

  /**
   * Create club activity (Club members only)
   */
  static async createActivity(clubId: string, activityData: {
    type: 'announcement' | 'activity' | 'requirement' | 'event' | 'discussion';
    title: string;
    content: string;
    priority: 'low' | 'medium' | 'high';
    tags?: string[];
    eventDate?: string;
    isPinned?: boolean;
  }): Promise<Activity> {
    const response = await apiClient.post(`/clubs/${clubId}/activities`, activityData);
    return handleApiSuccess(response);
  }

  /**
   * Update activity (Author or club officers only)
   */
  static async updateActivity(clubId: string, activityId: string, activityData: any): Promise<Activity> {
    const response = await apiClient.put(`/clubs/${clubId}/activities/${activityId}`, activityData);
    return handleApiSuccess(response);
  }

  /**
   * Delete activity (Author or club officers only)
   */
  static async deleteActivity(clubId: string, activityId: string): Promise<void> {
    const response = await apiClient.delete(`/clubs/${clubId}/activities/${activityId}`);
    handleApiSuccess(response);
  }

  /**
   * Like or unlike activity
   */
  static async toggleActivityLike(clubId: string, activityId: string): Promise<{ isLiked: boolean; likesCount: number }> {
    const response = await apiClient.post(`/clubs/${clubId}/activities/${activityId}/toggle-like`);
    return handleApiSuccess(response);
  }

  /**
   * Get activity comments
   */
  static async getActivityComments(clubId: string, activityId: string): Promise<ActivityComment[]> {
    const response = await apiClient.get(`/clubs/${clubId}/activities/${activityId}/comments`);
    return handleApiSuccess(response);
  }

  /**
   * Add comment to activity
   */
  static async addActivityComment(clubId: string, activityId: string, content: string): Promise<ActivityComment> {
    const response = await apiClient.post(`/clubs/${clubId}/activities/${activityId}/comments`, { content });
    return handleApiSuccess(response);
  }

  // ============ CLUB DISCUSSIONS ============

  /**
   * Get club discussions
   */
  static async getClubDiscussions(clubId: string): Promise<Discussion[]> {
    const response = await apiClient.get(`/clubs/${clubId}/discussions`);
    return handleApiSuccess(response);
  }

  /**
   * Create new discussion (Club members only)
   */
  static async createDiscussion(clubId: string, discussionData: {
    title: string;
    content: string;
    category: 'general' | 'technical' | 'events' | 'support';
  }): Promise<Discussion> {
    const response = await apiClient.post(`/clubs/${clubId}/discussions`, discussionData);
    return handleApiSuccess(response);
  }

  /**
   * Get discussion thread
   */
  static async getDiscussion(clubId: string, discussionId: string): Promise<Discussion> {
    const response = await apiClient.get(`/clubs/${clubId}/discussions/${discussionId}`);
    return handleApiSuccess(response);
  }

  /**
   * Add reply to discussion
   */
  static async addDiscussionReply(clubId: string, discussionId: string, content: string): Promise<DiscussionReply> {
    const response = await apiClient.post(`/clubs/${clubId}/discussions/${discussionId}/replies`, { content });
    return handleApiSuccess(response);
  }

  /**
   * Update discussion (Author or club officers only)
   */
  static async updateDiscussion(clubId: string, discussionId: string, discussionData: any): Promise<Discussion> {
    const response = await apiClient.put(`/clubs/${clubId}/discussions/${discussionId}`, discussionData);
    return handleApiSuccess(response);
  }

  /**
   * Delete discussion (Author or club officers only)
   */
  static async deleteDiscussion(clubId: string, discussionId: string): Promise<void> {
    const response = await apiClient.delete(`/clubs/${clubId}/discussions/${discussionId}`);
    handleApiSuccess(response);
  }

  /**
   * Pin/unpin discussion (Club officers only)
   */
  static async toggleDiscussionPin(clubId: string, discussionId: string): Promise<void> {
    const response = await apiClient.post(`/clubs/${clubId}/discussions/${discussionId}/toggle-pin`);
    handleApiSuccess(response);
  }

  // ============ UTILITY METHODS ============

  /**
   * Check if user is club member
   */
  static async isClubMember(clubId: string): Promise<boolean> {
    try {
      const response = await apiClient.get(`/clubs/${clubId}/membership-status`);
      const data = handleApiSuccess(response);
      return data.isMember;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get user's clubs
   */
  static async getMyClubs(): Promise<Club[]> {
    const response = await apiClient.get('/clubs/my-clubs');
    return handleApiSuccess(response);
  }

  /**
   * Upload club banner/logo
   */
  static async uploadClubImage(file: File, onProgress?: (progress: number) => void): Promise<string> {
    const response = await apiClient.uploadFile('/clubs/upload-image', file, onProgress);
    const data = handleApiSuccess(response);
    return data.imageUrl;
  }

  /**
   * Get club statistics (Club officers only)
   */
  static async getClubStats(clubId: string): Promise<{
    memberCount: number;
    applicationCount: number;
    activityCount: number;
    discussionCount: number;
  }> {
    const response = await apiClient.get(`/clubs/${clubId}/stats`);
    return handleApiSuccess(response);
  }

  /**
   * Get current user's club applications
   */
  static async getUserApplications(): Promise<{
    applications: Array<{
      applicationId: string;
      club: {
        id: string;
        name: string;
        description: string;
        logo: string;
        facultyMentor: { name: string; email: string };
      };
      status: 'pending' | 'approved' | 'rejected';
      score: number;
      percentage: number;
      motivation: string;
      appliedAt: string;
      reviewedAt?: string;
      feedback?: string;
    }>;
    totalApplications: number;
  }> {
    const response = await apiClient.get('/clubs/my-applications');
    return handleApiSuccess(response);
  }

  /**
   * Get all applications across all clubs (Faculty only)
   */
  static async getAllApplications(status?: 'pending' | 'approved' | 'rejected'): Promise<{
    applications: Array<{
      applicationId: string;
      club: {
        id: string;
        name: string;
        logo: string;
        facultyMentor: { name: string; email: string };
      };
      applicant: {
        id: string;
        name: string;
        email: string;
        branch: string;
        profilePicture?: string;
      };
      status: 'pending' | 'approved' | 'rejected';
      score: number;
      percentage: number;
      motivation: string;
      appliedAt: string;
      reviewedAt?: string;
      feedback?: string;
      reconsiderationRequested: boolean;
      reconsiderationMessage?: string;
    }>;
    totalApplications: number;
    summary: {
      pending: number;
      approved: number;
      rejected: number;
    };
  }> {
    let url = '/clubs/all-applications';
    if (status) {
      url += `?status=${status}`;
    }
    const response = await apiClient.get(url);
    return handleApiSuccess(response);
  }
}

export default ClubService;